import {
  OrderSheet,
  OrderSheetSet,
  OrderSheetStatus,
} from "@/features/order-sheets/order-sheet.type";
import AppButton from "@/features/line-sheet-sets/app-button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import { uniq } from "lodash";
import AppSelect from "@/features/line-sheet-sets/app-select";
import useBulkUpdateOrderSheetStatus, {
  BulkUpdateOrderSheetStatusPathParameter,
  BulkUpdateOrderSheetStatusRequest,
  BulkUpdateOrderSheetStatusResponse,
} from "@/features/order-status/hooks/use-bulk-update-order-sheet-status";
import useAppToasts, { useShowError } from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useIdentity from "@/features/ui/hooks/use-identity";
import useBulkUpdateOrderSheetSetStatus, {
  BulkUpdateOrderSheetSetStatusResponse,
} from "@/features/order-status/hooks/use-bulk-update-order-sheet-set-status";
import ConfirmByBoutiqueOrderSheetSetModal, {
  ConfirmByBoutiqueOrderSheetSetModalProps,
} from "@/features/order-status/modals/confirm-by-boutique-order-sheet-set-modal";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import { ConfirmByBoutiqueOrderSheetSetResponse } from "@/features/order-status/hooks/use-confirm-by-boutique-order-sheet-set";
import ToBeUpdatedOrderSheetList, {
  ToBeUpdatedOrderSheet,
} from "@/features/order-status/modals/to-be-updated-order-sheet-list";
import { LineSheetStatus } from "@/features/line-sheet-sets/line-sheet-set.types";
import BulkConfirmByAgencyOrderSheetsModal, {
  BulkConfirmByAgencyOrderSheetsModalProps,
} from "@/features/order-status/modals/bulk-confirm-by-agency-order-sheets-modal";
import { useTranslation } from "react-i18next";
import { toLower, toTitle } from "@/utils/case";
import ChangeOrderSheetStatusConfirmMessage from "@/features/order-status/change-order-sheet-status-confirm-message";
import BoutiqueShipOrderSheetSetModal, {
  BoutiqueShipOrderSheetSetModalProps,
} from "@/features/order-status/modals/boutique-ship-order-sheet-set-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { convertOrderStatusToNew } from "@/shared/utils";

export interface UpdateOrderSheetStatusItem {
  orderSheetSetId: number;
  id: number;
  name: string;
  suffix?: string;
  status: OrderSheet["status"];
  createdByCompany: Pick<OrderSheetSet["createdByCompany"], "name">;
  lineSheet: { id: number; status: LineSheetStatus };
  issuedBy: { name: string };
  toBeSubmitted: { id: number; name: string } | null;
}

export interface UpdateOrderSheetSetStatusItem {
  id: number;
  status: OrderSheetSet["status"];
  // 주문시트 상세(orderSheets)가 비어있을 때 오더시트세트 자체를 표시하기 위한 정보.
  name?: string;
  issuedBy?: { name: string };
  createdByCompany: Pick<OrderSheetSet["createdByCompany"], "name">;
  // 금액 통화(라인시트세트 통화) — 없으면 유로
  currency?: string;
  orderSheets: {
    orderedQuantity: number;
    orderedAmount: number;
    id: number;
    name: string;
    confirmedAmount: number;
    confirmedQuantity: number;
    issuedBy: { name: string };
    lineSheet: { id: number; status: LineSheetStatus };
    toBeSubmitted: { id: number; name: string } | null;
  }[];
}

export interface OrderSheetStatusUpdaterProps {
  items: UpdateOrderSheetStatusItem[] | UpdateOrderSheetSetStatusItem[];
  onUpdate: (
    response:
      | BulkUpdateOrderSheetSetStatusResponse
      | BulkUpdateOrderSheetStatusResponse
  ) => void;
  isBoutique?: boolean;
}

export function isBulkUpdateOrderSheetSetStatusResponse(
  response:
    | BulkUpdateOrderSheetSetStatusResponse
    | BulkUpdateOrderSheetStatusResponse
): response is BulkUpdateOrderSheetSetStatusResponse {
  return "orderSheetSets" in response;
}

export function isBulkUpdateOrderSheetStatusResponse(
  response:
    | BulkUpdateOrderSheetSetStatusResponse
    | BulkUpdateOrderSheetStatusResponse
): response is BulkUpdateOrderSheetStatusResponse {
  return "orderSheets" in response;
}

function isOrderSheetSetItems(
  items: UpdateOrderSheetStatusItem[] | UpdateOrderSheetSetStatusItem[]
): items is UpdateOrderSheetSetStatusItem[] {
  return items.length === 0 || "orderSheets" in items[0];
}

function isOrderSheetItems(
  items: UpdateOrderSheetStatusItem[] | UpdateOrderSheetSetStatusItem[]
): items is UpdateOrderSheetStatusItem[] {
  return items.length === 0 || "orderSheetSetId" in items[0];
}

export default function OrderSheetStatusUpdater({
  items,
  onUpdate,
  isBoutique = false,
}: OrderSheetStatusUpdaterProps) {
  const identity = useIdentity();
  const { openDialog, closeDialog } = useAppDialog();
  const { error: showError } = useAppToasts();
  const [value, setValue] = useState<OrderSheetStatus>();
  const { t, tUpper } = useI18nHelper();
  const itemsKey = useMemo(() => {
    let prefix = isOrderSheetSetItems(items)
      ? "OrderSheetSetItems_"
      : "OrderSheetItems_";
    return (
      prefix +
      items
        .map((item) => item.id)
        .sort((a, b) => a - b)
        .join(",")
    );
  }, [items]);

  const options = useMemo((): AppSelectOption<OrderSheetStatus>[] => {
    let toStatuses: OrderSheetStatus[] = [];
    const fromStatuses = uniq(items.map((item) => item.status));

    if (fromStatuses.length === 1) {
      if (isOrderSheetItems(items)) {
        const orderSheetSetIds = uniq(
          items.map((item) => item.orderSheetSetId)
        );
        if (orderSheetSetIds.length === 1) {
          const fromStatus = fromStatuses[0];
          if (fromStatus === "ORDERED_TO_AGENCY") {
            toStatuses = [
              "ORDERED_TO_BOUTIQUE",
              "CANCELED",
              // "MODIFICATION_REQUIRED",
            ];
          } else if (fromStatus === "WORKING_DRAFT") {
            toStatuses = ["CANCELED"];
          } else if (fromStatus === "AGENCY_CONFIRMED") {
            toStatuses = ["WORKING_DRAFT", "CANCELED", "ORDERED_TO_BOUTIQUE"];
          } else if (fromStatus === "ORDERED_TO_BOUTIQUE") {
            if (isBoutique) {
              toStatuses = ["RESUBMIT_REQUIRED", "BOUTIQUE_CONFIRMED"];
            } else {
              toStatuses = [
                "WORKING_DRAFT",
                "RESUBMIT_REQUIRED",
                "BOUTIQUE_CONFIRMED",
                "CANCELED",
                // "MODIFICATION_REQUIRED",
              ];
            }
          } else if (fromStatus === "RESUBMIT_REQUIRED") {
            if (isBoutique) {
              toStatuses = [];
            } else {
              toStatuses = [
                "ORDERED_TO_BOUTIQUE",
                "RESUBMIT_COMPLETED",
                "CANCELED",
              ];
            }
          } else if (fromStatus === "RESUBMIT_COMPLETED") {
            if (isBoutique) {
              toStatuses = [];
            } else {
              toStatuses = ["ORDERED_TO_BOUTIQUE", "CANCELED"];
            }
          } else if (fromStatus === "BOUTIQUE_CONFIRMED") {
            // 부티크는 바이어 승인 필요 상태에서 더 바꿀 수 없다 — 바이어 승인을 기다린다
            // 에이전시는 바이어가 수정을 요청하면 오더 협의 중으로 되돌리고, 바이어 대신 오더 확정도 할 수 있다
            // (서버 bulk-update-status 가 허용하는 전이와 같다 — 최종 완료는 거부되므로 뺐다)
            toStatuses = isBoutique
              ? []
              : ["ORDERED_TO_BOUTIQUE", "BUYER_CONFIRMED"];
          } else if (fromStatus === "BUYER_CONFIRMED") {
            toStatuses = ["TRANSPORTATION_ING"];
          } else if (fromStatus === "TRANSPORTATION_ING") {
            toStatuses = ["TRANSPORTATION_COMPLETED"];
          } else if (fromStatus === "TRANSPORTATION_COMPLETED") {
            toStatuses = ["COMPLETED"];
          }
        }
      } else {
        const fromStatus = fromStatuses[0];
        if (fromStatus === "ORDERED_TO_AGENCY") {
          toStatuses = [
            "WORKING_DRAFT",
            "ORDERED_TO_BOUTIQUE",
            "CANCELED",
            // "MODIFICATION_REQUIRED",
          ];
        } else if (fromStatus === "AGENCY_CONFIRMED") {
          toStatuses = ["WORKING_DRAFT", "CANCELED", "ORDERED_TO_BOUTIQUE"];
        } else if (fromStatus === "ORDERED_TO_BOUTIQUE") {
          if (isBoutique) {
            toStatuses = ["RESUBMIT_REQUIRED", "BOUTIQUE_CONFIRMED"];
          } else {
            toStatuses = [
              "WORKING_DRAFT",
              "RESUBMIT_REQUIRED",
              "BOUTIQUE_CONFIRMED",
              "CANCELED",
              // "MODIFICATION_REQUIRED",
            ];
          }
        } else if (fromStatus === "RESUBMIT_REQUIRED") {
          if (isBoutique) {
            toStatuses = [];
          } else {
            toStatuses = [
              "ORDERED_TO_BOUTIQUE",
              "RESUBMIT_COMPLETED",
              "CANCELED",
            ];
          }
        } else if (fromStatus === "RESUBMIT_COMPLETED") {
          if (isBoutique) {
            toStatuses = ["ORDERED_TO_BOUTIQUE"];
          } else {
            toStatuses = ["ORDERED_TO_BOUTIQUE", "CANCELED"];
          }
        } else if (fromStatus === "BOUTIQUE_CONFIRMED") {
          // 부티크는 바이어 승인 필요 상태에서 더 바꿀 수 없다 — 바이어 승인을 기다린다
          // 에이전시는 바이어가 수정을 요청하면 오더 협의 중으로 되돌리고, 바이어 대신 오더 확정도 할 수 있다
          // (서버 bulk-update-status 가 허용하는 전이와 같다 — 최종 완료는 거부되므로 뺐다)
          toStatuses = isBoutique
            ? []
            : ["ORDERED_TO_BOUTIQUE", "BUYER_CONFIRMED"];
        } else if (fromStatus === "BUYER_CONFIRMED") {
          toStatuses = ["TRANSPORTATION_ING"];
        } else if (fromStatus === "TRANSPORTATION_ING") {
          toStatuses = ["TRANSPORTATION_COMPLETED"];
        } else if (fromStatus === "TRANSPORTATION_COMPLETED") {
          toStatuses = ["COMPLETED"];
        }
      }
    }

    return toStatuses.map((toStatus) => {
      return {
        // name: tUpper(`order_sheet.status.${toLower(toStatus)}`),
        name: tUpper(
          `order_history:${toLower(convertOrderStatusToNew(toStatus))}`
        ),
        value: toStatus,
      };
    });
  }, [isBoutique, items, tUpper]);

  useEffect(() => {
    setValue(undefined);
  }, [itemsKey]);

  const toBeUpdateOrderSheets = useMemo((): ToBeUpdatedOrderSheet[] => {
    if (isOrderSheetSetItems(items)) {
      return items.flatMap((orderSheetSet) => {
        // 주문시트 상세가 있으면 그대로 표시.
        if (orderSheetSet.orderSheets.length > 0) {
          return orderSheetSet.orderSheets.map((orderSheet) => {
            return {
              id: orderSheet.id,
              name: orderSheet.name,
              orderedBy: orderSheetSet.createdByCompany.name,
              issuedBy: orderSheet.issuedBy.name,
              toBeSubmitted: orderSheet.toBeSubmitted,
            };
          });
        }
        // 주문시트 상세가 없으면(목록 응답 등) 선택된 오더시트세트 자체를 한 줄로 표시.
        // 발행사 칼럼은 toBeSubmitted.name으로 렌더되므로 발행 부티크명을 거기에 넣는다.
        return [
          {
            id: orderSheetSet.id,
            name: orderSheetSet.name ?? `#${orderSheetSet.id}`,
            orderedBy: orderSheetSet.createdByCompany.name,
            issuedBy: orderSheetSet.issuedBy?.name ?? "",
            toBeSubmitted: orderSheetSet.issuedBy
              ? { id: orderSheetSet.id, name: orderSheetSet.issuedBy.name }
              : null,
          },
        ];
      });
    } else {
      return items.map((orderSheet) => {
        return {
          id: orderSheet.id,
          name: orderSheet.name,
          orderedBy: orderSheet.createdByCompany.name,
          issuedBy: orderSheet.issuedBy.name,
          toBeSubmitted: orderSheet.toBeSubmitted,
        };
      });
    }
  }, [items]);

  const dialogMessage = useMemo(() => {
    return (
      <ToBeUpdatedOrderSheetList
        header={<ChangeOrderSheetStatusConfirmMessage status={value} />}
        orderSheets={toBeUpdateOrderSheets}
      />
    );
  }, [toBeUpdateOrderSheets, value]);

  const {
    isLoading: isBulkUpdateOrderSheetStatusLoading,
    data: bulkUpdateOrderSheetStatusData,
    error: bulkUpdateOrderSheetStatusError,
    fire: fireBulkUpdateOrderSheetStatus2,
  } = useBulkUpdateOrderSheetStatus();

  const fireBulkUpdateOrderSheetStatus = useCallback(
    async (
      parameter: BulkUpdateOrderSheetStatusPathParameter,
      request: BulkUpdateOrderSheetStatusRequest
    ) => {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      return fireBulkUpdateOrderSheetStatus2(parameter, request);
    },
    [fireBulkUpdateOrderSheetStatus2]
  );

  const {
    isLoading: isBulkUpdateOrderSheetSetStatusLoading,
    data: bulkUpdateOrderSheetSetStatusData,
    error: bulkUpdateOrderSheetSetStatusError,
    fire: fireBulkUpdateOrderSheetSetStatus2,
  } = useBulkUpdateOrderSheetSetStatus();


  useShowError(bulkUpdateOrderSheetSetStatusError, (e) => showError(e, {
    i18n: {
      keyPrefix: "bulk_update_order_sheet_set_status.error",
      ns: "order_sheet_sets",
    },
  }));

  const fireBulkUpdateOrderSheetSetStatus = useCallback(
    async (parameter: any, request: any) => {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      return fireBulkUpdateOrderSheetSetStatus2(parameter, request);
    },
    [fireBulkUpdateOrderSheetSetStatus2]
  );


  const [
    confirmByBoutiqueOrderSheetSetData,
    setConfirmByBoutiqueOrderSheetSetData,
  ] = useState<ConfirmByBoutiqueOrderSheetSetResponse>();

  const {
    isOpen: isBulkConfirmByAgencyOrderSheetsModalOpen,
    onClose: onBulkConfirmByAgencyOrderSheetsModalClose,
    data: bulkConfirmByAgencyOrderSheetsModalData,
    openWithData: openBulkConfirmByAgencyOrderSheetsModalWithData,
  } = useDisclosureWithData<BulkConfirmByAgencyOrderSheetsModalProps>();

  const {
    isOpen: isConfirmByBoutiqueOrderSheetSetModalOpen,
    onClose: onConfirmByBoutiqueOrderSheetSetModalClose,
    onOpen: onConfirmByBoutiqueOrderSheetSetModalOpen,
    data: confirmByBoutiqueOrderSheetSetModalData,
    openWithData: openConfirmByBoutiqueOrderSheetSetModalWithData,
  } = useDisclosureWithData<ConfirmByBoutiqueOrderSheetSetModalProps>();

  // 부티크의 운송 중 전환 — 배송사·송장번호를 받아 운송/통관 건을 만들고 그 등록이 오더를 운송 중으로 바꾼다.
  const {
    isOpen: isBoutiqueShipModalOpen,
    onClose: onBoutiqueShipModalClose,
    data: boutiqueShipModalData,
    openWithData: openBoutiqueShipModalWithData,
  } = useDisclosureWithData<BoutiqueShipOrderSheetSetModalProps>();

  useEffect(() => {
    if (
      bulkUpdateOrderSheetStatusData ||
      bulkUpdateOrderSheetSetStatusData ||
      confirmByBoutiqueOrderSheetSetData
    ) {
      // if (dialogRef.current) {
      //   closeDialog(dialogRef.current);
      // }
      setValue(undefined);
      if (bulkUpdateOrderSheetSetStatusData) {
        onUpdate(bulkUpdateOrderSheetSetStatusData);
      } else if (bulkUpdateOrderSheetStatusData) {
        onUpdate(bulkUpdateOrderSheetStatusData);
      } else if (confirmByBoutiqueOrderSheetSetData) {
        onUpdate({
          orderSheetSets: [confirmByBoutiqueOrderSheetSetData],
        });
      }
    }
  }, [
    bulkUpdateOrderSheetStatusData,
    bulkUpdateOrderSheetSetStatusData,
    confirmByBoutiqueOrderSheetSetData,
    closeDialog,
    onUpdate,
  ]);

  return (
    <>
      <AppSelect<OrderSheetStatus>
        width={"144px"}
        name={toTitle(t("status"))}
        options={options}
        isDisabled={options.length === 0}
        value={value}
        onSelect={(v) => setValue(v)}
      />
      <AppButton
        isDisabled={value === undefined}
        isLoading={
          isBulkUpdateOrderSheetStatusLoading ||
          isBulkUpdateOrderSheetSetStatusLoading
        }
        onClick={async () => {
          if (value === "BOUTIQUE_CONFIRMED") {
            if (
              identity &&
              (identity.company?.type === "AGENCY" ||
                identity.company?.type === "BOUTIQUE") &&
              isOrderSheetSetItems(items)
            ) {
              if (items.length === 1) {
                openConfirmByBoutiqueOrderSheetSetModalWithData((prev) => {
                  return {
                    by: identity.company!.type,
                    companyId: identity.company?.id!,
                    orderSheetSetId: items[0].id,
                    orderSheetSet: items[0],
                    toBeUpdateOrderSheets,
                    onConfirm: setConfirmByBoutiqueOrderSheetSetData,
                  };
                });
              } else {
                showError({
                  error: {
                    code: -1,
                    status: "NOT SUPPORTED OPERATION",
                    message: "Confirm by boutique is not supported for bulk.",
                  },
                });
              }
            } else {
              showError({
                error: {
                  code: -1,
                  status: "NOT SUPPORTED OPERATION",
                  message: "부티크 컨펌 완료 처리는 리스트에서만 가능합니다.",
                },
              });
            }
          } else if (
            value === "TRANSPORTATION_ING" &&
            identity?.company?.type === "BOUTIQUE"
          ) {
            // 부티크는 상태만 바꾸지 않고 운송/통관 건을 함께 만든다 — 오더(세트) 하나씩만.
            const orderSheetSetId = isOrderSheetSetItems(items)
              ? items.length === 1
                ? items[0].id
                : undefined
              : uniq(items.map((item) => item.orderSheetSetId)).length === 1
              ? items[0].orderSheetSetId
              : undefined;
            if (orderSheetSetId === undefined) {
              showError({
                error: {
                  code: -1,
                  status: "NOT SUPPORTED OPERATION",
                  message: "운송 중 변경은 오더 하나씩만 할 수 있습니다.",
                },
              });
            } else {
              openBoutiqueShipModalWithData(() => ({
                boutiqueId: identity.company!.id,
                orderSheetSetId,
                toBeUpdateOrderSheets,
                onConfirm: (response) => {
                  setValue(undefined);
                  onUpdate({ orderSheetSets: [response] });
                },
              }));
            }
          } else if (value === "AGENCY_CONFIRMED") {
            if (identity?.company?.type === "AGENCY") {
              if (isOrderSheetItems(items)) {
                openBulkConfirmByAgencyOrderSheetsModalWithData((prev) => {
                  return {
                    agencyId: identity.company!.id,
                    orderSheetSetId: items[0].orderSheetSetId,
                    orderSheets: items,
                    issuedByCompany: items[0].createdByCompany,
                    createdByCompany: items[0].createdByCompany,
                    onConfirm: onUpdate,
                  };
                });
              } else if (isOrderSheetSetItems(items)) {
                //헬이구만ㅇㅋ
                openBulkConfirmByAgencyOrderSheetsModalWithData((prev) => {
                  return {
                    agencyId: identity.company!.id,
                    orderSheetSetId: items[0].id,
                    orderSheets: items.flatMap((item) => {
                      return item.orderSheets;
                    }),
                    issuedByCompany: items[0].createdByCompany,
                    createdByCompany: items[0].createdByCompany,
                    onConfirm: onUpdate,
                  };
                });
              }
            }
          } else {
            const isConfirmed = await openDialog({
              title: toTitle(t("change_status")),
              message: dialogMessage,
              confirmName: toTitle(t("change")),
              onConfirm: async () => {
                if (
                  value &&
                  identity &&
                  (identity.company?.type === "AGENCY" ||
                    identity.company?.type === "BOUTIQUE")
                ) {
                  if (isOrderSheetItems(items)) {
                    return fireBulkUpdateOrderSheetStatus(
                      {
                        by: identity.company.type,
                        companyId: identity.company.id,
                        orderSheetSetId: items[0].orderSheetSetId,
                      },
                      {
                        status: value,
                        orderSheetIds: items.map((item) => item.id),
                      }
                    );
                  } else if (isOrderSheetSetItems(items)) {
                    return fireBulkUpdateOrderSheetSetStatus(
                      {
                        by: identity.company.type,
                        companyId: identity.company.id,
                      },
                      {
                        status: value,
                        orderSheetSetIds: items.map((item) => item.id),
                      }
                    );
                  }
                }
              },
            });
          }
        }}
      >
        {toTitle(t("change_status"))}
      </AppButton>
      {confirmByBoutiqueOrderSheetSetModalData && (
        <ConfirmByBoutiqueOrderSheetSetModal
          isOpen={isConfirmByBoutiqueOrderSheetSetModalOpen}
          onClose={onConfirmByBoutiqueOrderSheetSetModalClose}
          {...confirmByBoutiqueOrderSheetSetModalData}
        />
      )}
      {isBoutiqueShipModalOpen && boutiqueShipModalData && (
        <BoutiqueShipOrderSheetSetModal
          isOpen={isBoutiqueShipModalOpen}
          onClose={onBoutiqueShipModalClose}
          {...boutiqueShipModalData}
        />
      )}
      {isBulkConfirmByAgencyOrderSheetsModalOpen &&
        bulkConfirmByAgencyOrderSheetsModalData && (
          <BulkConfirmByAgencyOrderSheetsModal
            isOpen={isBulkConfirmByAgencyOrderSheetsModalOpen}
            onClose={onBulkConfirmByAgencyOrderSheetsModalClose}
            {...bulkConfirmByAgencyOrderSheetsModalData}
          />
        )}
    </>
  );
}
